import { Permissions } from './../../../core/constants/permissions';
import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { PatientDetailActivityComponent } from './patient-detail-activity/patient-detail-activity.component';
import { PatientDetailVisitsComponent } from './patient-detail-visits/patient-detail-visits.component';
import { PatientDetail } from "../../../core/models/patient-detail.model";
import { PatientService } from "../../../core/services/patient.service";
import { LogHelper } from "../../../core/helpers/log.helper";
import { AuthService } from "../../../core/services/auth.service";
import { AlertService } from "../../../shared/alert/alert.service";
import { TemplateService } from "../../../core/services/template.service";
import { ModalComponent } from "../../../shared/modal/modal.component";
import { PatientBasicInfo } from '../../../core/models/patient.model';
import { PatientEditModalComponent } from "../../../shared/patient-edit-modal/patient-edit-modal.component";
import { PatientSetPasswordModalComponent } from "../patient-set-password-modal/patient-set-password-modal.component";
import { RoleNames } from "../../../core/constants/role-names";
import { NavigationService, NavRoute } from "../../../core/services/navigation.service";
import { PatientApproveModalComponent } from "../patient-approve-modal/patient-approve-modal.component";
import { requiredNoWhitespaceValidator } from "../../../core/validators/no-whitespace.validator";

@Component({
  selector: 'app-patient-detail',
  templateUrl: './patient-detail.component.html',
  styleUrls: ['./patient-detail.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PatientDetailComponent implements OnInit {
  @ViewChild('patientApproveModal') patientApproveModal: PatientApproveModalComponent;
  @ViewChild('notificationModal') notificationModal: ModalComponent;
  @ViewChild('activityTab') activityTab: PatientDetailActivityComponent;
  @ViewChild('visitsTab') visitsTab: PatientDetailVisitsComponent;
  @ViewChild('patientEditModal') patientEditModal: PatientEditModalComponent;
  @ViewChild('setPasswordModal') setPasswordModal: PatientSetPasswordModalComponent;

  selectedTab = 'visits';
  patientTrialId: string;
  patient = new PatientDetail();

  get currentPatientTrial() {
    return this.patient?.trials?.find(t => t.trialId === this.patient.trialId);
  }

  patientBasicInfo = new PatientBasicInfo();
  Permissions = Permissions;
  patientLoaded = false;
  resyncing = false;

  notificationForm: UntypedFormGroup;
  notificationFormIsProcessing = false;

  tabs: { id: string, permissions: string[] }[] = [
    { id: 'visits', permissions: [Permissions.PatientVisitsRead] },
    { id: 'expenses', permissions: [Permissions.PatientExpenseWrite, Permissions.PatientExpenseRead] },
    { id: 'additional-details', permissions: [Permissions.PatientDetailsRead] },
    { id: 'notes', permissions: [Permissions.PatientNotesRead] },
    { id: 'activity', permissions: [Permissions.PatientActivityRead] },
    { id: 'payment-info', permissions: [Permissions.PatientPaymentInfoRead] },
    { id: 'documents', permissions: [Permissions.PatientDocumentsRead] },
    { id: 'diary', permissions: [Permissions.PatientEdiaryRead] },
    { id: 'generic-diary', permissions: [Permissions.PatientEdiaryRead] },
  ];

  constructor(public authService: AuthService, private _navService: NavigationService, private readonly templateService: TemplateService,
    private readonly location: Location, private readonly patientService: PatientService,
    private readonly activatedRoute: ActivatedRoute, private readonly alertService: AlertService,
    private router: Router) { }

  ngOnInit() {
    this.templateService.showHeader();

    this.activatedRoute.params.subscribe(params => {
      this.patientTrialId = params.id;
      this.loadPatient()
    });

    this.notificationForm = new UntypedFormGroup({
      title: new UntypedFormControl('', requiredNoWhitespaceValidator()),
      content: new UntypedFormControl('', requiredNoWhitespaceValidator())
    });
  }

  setDefaultTab() {
    // Select tab based on URL fragment if present
    this.activatedRoute.fragment.subscribe((fragment) => {
      if (fragment != null) {
        this.onSelectTab(fragment);
        return;
      }
    });

    // If the patient is pending, select the pending tab
    if (this.patient.patientState === 'Pending') {
      this.onSelectTab('pending');
      return;
    }

    // Check NavState to see if we need to restore a previous tab
    const navState = this._navService.get(NavRoute.Patient);
    if (navState && navState.lastId === this.patient.patientTrialId) {
      this.selectedTab = navState.fragment;
      return;
    }

    // Otherwise, select the first tab that the user has permission to view
    for (const tab of this.tabs) {
      if (tab.permissions.some(permission => this.authService.hasPermission(permission))) {
        if (tab.id === 'diary') {
          if (this.patient.diaryEnabled) {
            this.onSelectTab(tab.id);
            break;
          }
        } else if (tab.id === 'generic-diary') {
          if (this.patient.genericDiaryEnabled) {
            this.onSelectTab(tab.id);
            break;
          }
        } else {
          this.onSelectTab(tab.id);
          break;
        }
      }
    }
  }

  loadPatient() {
    this.patientService.retrievePatientDetailFromPatientTrialId(this.patientTrialId).subscribe({
      next: patient => {
        this.patient = patient;
        this.patientLoaded = true;
        this.patientBasicInfo = {
          firstName: patient.firstname,
          lastName: patient.lastname,
          email: patient.email
        } as PatientBasicInfo

        this.setDefaultTab();

        if (this.selectedTab === 'visits') {
          this.visitsTab?.loadVisits();
        }
      },
      error: error => {
        LogHelper.log(error);
        if (error.status === 403) {
          this.alertService.showWarningAlert('You do not have permission to view this patient.');
          void this.router.navigate(['/patient']);
        } else {
          this.alertService.showWarningAlert('Unable to load patient information!');
        }
      }
    });
  }

  onBackClick() {
    this.location.back();
  }

  onSelectTab(selectedTab: string) {
    this.selectedTab = selectedTab;

    this._navService.track({ route: NavRoute.Patient, lastId: this.patient.patientTrialId, fragment: selectedTab });
  }

  onOpenNotificationPopup() {
    this.notificationForm.reset();
    this.notificationModal.show();
  }

  onSendToPatient() {
    if (!this.patient.email) {
      this.alertService.showWarningAlert('No email address associated with patient.');
    } else {
      this.patientService.sendClaimAccountEmail(this.patient.id).subscribe({
        next: () => {
          this.alertService.showSuccessAlert(`Email sent to ${this.patient.email}`);
        },
        error: error => {
          LogHelper.log(error);
          this.alertService.showWarningAlert('Sorry, there was a problem sending an email to the patient.');
        }
      });
    }
  }

  onSubmitNotification() {
    const form = this.notificationForm;

    if (form.valid) {
      this.notificationFormIsProcessing = true;
      this.patientService.sendPatientNotification(
        this.patient.id,
        this.patient.trialId,
        this.notificationForm.get('title').value,
        this.notificationForm.get('content').value).subscribe({
          next: () => {
            this.notificationFormIsProcessing = false;
            this.alertService.showSuccessAlert('Notification successfully sent to patient.');
            this.notificationModal.hide();

            if (this.selectedTab === 'activity') {
              this.activityTab.refreshView();
            }

            this.notificationForm.reset();
          },
          error: error => {
            this.notificationFormIsProcessing = false;
            this.alertService.showWarningAlert(error.error ? error.error.title : 'Sorry, something went wrong!');
          }
        });
    }
  }

  getLoadingText() {
    return 'Loading patient...';
  }

  onHandleRejectPatient() {
    this.patientService.rejectPatient(this.patient.patientTrialId).subscribe({
      next: () => {
        this.loadPatient();
        this.alertService.showSuccessAlert('Patient Rejected Successfully.');
      },
      error: error => {
        LogHelper.log(error);
        this.alertService.showWarningAlert('Sorry, there was a problem performing that action.');
      }
    });
  }

  resyncPatient() {
    this.resyncing = true;
    this.patientService.resyncPatient(this.patientTrialId).subscribe({
      next: () => {
        this.patient.syncFailed = false;
        this.resyncing = false;
        this.alertService.showSuccessAlert('Patient queued for resynchronisation.');
      },
      error: error => {
        LogHelper.log(error);
        this.resyncing = false;
        this.alertService.showWarningAlert('Sorry, there was a problem resyncing the patient.');
      }
    });
  }

  protected readonly RoleNames = RoleNames;
}
